import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Users, DollarSign, MessageSquare, Briefcase, 
  Calendar, LogOut, ExternalLink, Search,
  ArrowLeft, Bell, LayoutDashboard, Settings,
  FileText, Building2, CalendarDays, ChevronRight, Globe, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const toolCategories = [
  {
    id: 'recruiting',
    name: 'Recruiting & ATS',
    icon: Users,
    color: 'from-blue-500 to-blue-600',
    border: 'border-blue-200',
    tools: [
      { name: 'Manatal', url: 'https://app.manatal.com', description: 'Applicant tracking system (ATS)' },
      { name: 'LinkedIn', url: 'https://www.linkedin.com', description: 'Professional networking' },
      { name: 'Sales Navigator', url: 'https://www.linkedin.com/sales', description: 'LinkedIn premium for sales' },
      { name: 'Instantly.ai', url: 'https://instantly.ai', description: 'Cold email outreach & automation' },
      { name: 'Dux-Soup', url: 'https://www.dux-soup.com', description: 'LinkedIn automation & lead gen' },
      { name: 'Indeed', url: 'https://employers.indeed.com', description: 'Job posting & candidate search' },
      { name: 'ZipRecruiter', url: 'https://www.ziprecruiter.com/employer', description: 'Job distribution platform' },
      { name: 'Monster', url: 'https://hiring.monster.com', description: 'Job board & resume search' },
      { name: 'Glassdoor', url: 'https://www.glassdoor.com/member/home/index.htm', description: 'Employer branding & jobs' },
    ]
  },
  {
    id: 'crm',
    name: 'CRM & Client Management',
    icon: Briefcase,
    color: 'from-purple-500 to-purple-600',
    border: 'border-purple-200',
    tools: [
      { name: 'Airtable', url: 'https://airtable.com', description: 'Flexible database & CRM' },
    ]
  },
  {
    id: 'finance',
    name: 'Finance & Accounting',
    icon: DollarSign,
    color: 'from-emerald-500 to-emerald-600',
    border: 'border-emerald-200',
    tools: [
      { name: 'Zoho Books', url: 'https://books.zoho.com', description: 'Accounting & invoicing' },
      { name: 'Stripe', url: 'https://dashboard.stripe.com', description: 'Payment processing' },
    ]
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure',
    icon: Globe,
    color: 'from-slate-500 to-slate-600',
    border: 'border-slate-200',
    tools: [
      { name: 'Namecheap', url: 'https://www.namecheap.com', description: 'Domain management' },
    ]
  },
  {
    id: 'productivity',
    name: 'Project Management',
    icon: LayoutDashboard,
    color: 'from-amber-500 to-amber-600',
    border: 'border-amber-200',
    tools: [
      { name: 'Asana', url: 'https://app.asana.com', description: 'Task & project management' },
      { name: 'Trello', url: 'https://trello.com', description: 'Visual project boards' },
      { name: 'Notion', url: 'https://www.notion.so', description: 'Notes & documentation' },
    ]
  },
  {
    id: 'communication',
    name: 'Communication',
    icon: MessageSquare,
    color: 'from-pink-500 to-pink-600',
    border: 'border-pink-200',
    tools: [
      { name: 'Zoho Bookings', url: 'https://bookings.zoho.com', description: 'Appointment scheduling' },
      { name: 'Slack', url: 'https://slack.com', description: 'Team messaging' },
      { name: 'Zoom', url: 'https://zoom.us', description: 'Video conferencing' },
      { name: 'Microsoft Teams', url: 'https://teams.microsoft.com', description: 'Team collaboration' },
    ]
  },
  {
    id: 'marketing',
    name: 'Marketing & Outreach',
    icon: Calendar,
    color: 'from-cyan-500 to-cyan-600',
    border: 'border-cyan-200',
    tools: [
      { name: 'Canva', url: 'https://www.canva.com', description: 'Design & graphics' },
      { name: 'Mailchimp', url: 'https://mailchimp.com', description: 'Email marketing' },
      { name: 'Buffer', url: 'https://buffer.com', description: 'Social media scheduling' },
    ]
  },
  {
    id: 'ai-tools',
    name: 'AI Tools',
    icon: Zap,
    color: 'from-emerald-500 to-emerald-600',
    border: 'border-emerald-200',
    tools: [
      { name: 'ChatGPT', url: 'https://chat.openai.com', description: 'AI assistant' },
    ]
  },
];

const BackOfficeTools = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('backoffice_token');
    const userData = localStorage.getItem('backoffice_user');
    if (!token || !userData) { navigate('/backoffice/login'); return; }
    setUser(JSON.parse(userData));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('backoffice_token');
    localStorage.removeItem('backoffice_user');
    toast.success('Logged out successfully');
    navigate('/backoffice/login');
  };

  const filteredCategories = toolCategories.map(category => ({
    ...category,
    tools: category.tools.filter(tool =>
      tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category =>
    selectedCategory ? category.id === selectedCategory : category.tools.length > 0
  );

  const totalTools = toolCategories.reduce((sum, cat) => sum + cat.tools.length, 0);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex">
      {/* Sidebar */}
      <aside className="w-56 bg-[#0A1F44] text-white flex-shrink-0 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <img
            src="https://customer-assets.emergentagent.com/job_talent-delivery-2/artifacts/n21hqg7x_1000003920%20%281%29.png"
            alt="Omnia Paratus"
            className="h-8"
          />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <Link to="/backoffice" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
          <Link to="/backoffice/applications" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <FileText className="h-4 w-4" /> Applications
          </Link>
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-lg">
            <Settings className="h-4 w-4 text-[#D4AF37]" /> Tools
          </div>
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0A1F44] font-bold text-sm">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm text-gray-300 truncate">{user.name}</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-red-400 transition-colors w-full">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#f7f8fa]">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#0A1F44]">Business Tools</h1>
              <p className="text-sm text-gray-500">{totalTools} tools across {toolCategories.length} categories</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-[#0A1F44] placeholder-gray-400 focus:outline-none focus:border-[#D4AF37]/60 focus:ring-1 focus:ring-[#D4AF37]/30 transition-all"
                data-testid="tools-search-input"
              />
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                !selectedCategory
                  ? 'bg-[#D4AF37] text-[#0A1F44]'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              All
            </button>
            {toolCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-[#D4AF37] text-[#0A1F44]'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </header>

        {/* Tools Grid */}
        <main className="flex-1 p-8 overflow-auto">
          <div className="space-y-8">
            {filteredCategories.map(category => (
              <div key={category.id}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                    <category.icon className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A1F44]">{category.name}</h3>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{category.tools.length}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {category.tools.map(tool => (
                    <a
                      key={`${category.id}-${tool.name}`}
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group relative flex items-center gap-4 p-4 bg-white border ${category.border} rounded-xl shadow-sm hover:shadow-md hover:border-[#D4AF37]/60 transition-all duration-200`}
                      data-testid={`tool-${tool.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[#0A1F44] group-hover:text-[#8a6d1a] transition-colors truncate">
                          {tool.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{tool.description}</p>
                      </div>
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            ))}

            {filteredCategories.length === 0 && (
              <div className="text-center py-16">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-lg">No tools found</p>
                <p className="text-gray-400 text-sm mt-1">Try a different search term</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default BackOfficeTools;
